import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_WORK_ORDER, UPDATE_WORK_ORDER_STATUS } from '../../graphql/mutations';
import { toast } from 'react-hot-toast';

const CompleteWorkOrderModal = ({ workOrder, onClose, onComplete }) => {
  const [completionNotes, setCompletionNotes] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const [updateWorkOrder] = useMutation(UPDATE_WORK_ORDER);
  const [updateWorkOrderStatus] = useMutation(UPDATE_WORK_ORDER_STATUS);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    textareaRef.current?.focus();
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isNotesEmpty = !completionNotes.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNotesEmpty) {
      setError('Completion notes are required before closing out this work order.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const existingDescription = workOrder.description || '';
      const updatedDescription = `${existingDescription}\n\n--- Completion Notes ---\n${completionNotes.trim()}`;

      await updateWorkOrder({
        variables: {
          id: workOrder.id,
          input: {
            id: workOrder.id,
            description: updatedDescription,
          },
        },
      });

      await updateWorkOrderStatus({
        variables: {
          id: workOrder.id,
          status: 'COMPLETED',
        },
      });

      toast.success('Work order completed successfully');
      onComplete();
    } catch (err) {
      setError(err.message || 'Failed to complete work order. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Complete Work Order"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Complete Work Order</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
            tabIndex={0}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex">
              <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-800">
                Please provide more details about what was completed for this work order. This will be used for billing purposes to describe the details of the job.
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="completionNotes" className="block text-sm font-medium text-gray-700 mb-1">
              Completion Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={textareaRef}
              id="completionNotes"
              value={completionNotes}
              onChange={(e) => {
                setCompletionNotes(e.target.value);
                if (error) setError('');
              }}
              placeholder="Describe what was done, materials used, issues found, etc."
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-vertical"
              disabled={isSubmitting}
              aria-required="true"
            />
          </div>

          <div className="flex justify-end space-x-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isNotesEmpty}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                isSubmitting || isNotesEmpty
                  ? 'bg-green-300 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Completing...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Complete Work Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteWorkOrderModal;
